import { ButtonBase, IconButton } from "@mui/material"
import { Check, ChevronRight, FileCopy } from "@mui/icons-material"
import classNames from "classnames"
import LoadingDots from "components/loading-dots"
import PropTypes from "prop-types"
import { useMemo, useState } from "react"
import { copyToClipboard } from "helpers/clipboard"
import { TOOL_PART_TYPE_PREFIX, isToolPart } from "../../helpers/tool-parts"
import useSmoothRevealText from "../../hooks/use-smooth-reveal-text"
import "./style.scss"

const RUNNING_TOOL_STATES = new Set(["input-streaming", "input-available"])

function getToolNameFromPart(part) {
  return part.type === "dynamic-tool" ? part.toolName : part.type.slice(TOOL_PART_TYPE_PREFIX.length)
}

export function countToolCalls(parts) {
  return parts.filter(isToolPart).length
}

export function formatToolCallCount(count) {
  return `${count} ${count === 1 ? "tool call" : "tool calls"}`
}

export const TIMELINE_PART_SHAPE = PropTypes.shape({
  type: PropTypes.string,
  text: PropTypes.string,
  toolCallId: PropTypes.string,
  toolName: PropTypes.string,
  state: PropTypes.string,
  output: PropTypes.shape({
    status: PropTypes.string,
    result: PropTypes.string,
    resultTruncated: PropTypes.bool,
  }),
  errorText: PropTypes.string,
})

function formatToolResult(result) {
  try {
    return JSON.stringify(JSON.parse(result), null, 2)
  } catch {
    return result
  }
}

function ResolvedToolLine({ part }) {
  const [resultExpanded, setResultExpanded] = useState(false)
  const [resultCopied, setResultCopied] = useState(false)
  const { result, resultTruncated } = part.output ?? {}

  const formattedResult = useMemo(() => (resultExpanded ? formatToolResult(result) : null), [resultExpanded, result])

  if (part.state === "output-error") {
    return <div className="step step-error">Failed {getToolNameFromPart(part)}{part.errorText ? `: ${part.errorText}` : ""}</div>
  }

  if (!result) {
    return <div className="step">Ran {getToolNameFromPart(part)}</div>
  }

  function handleCopyResult() {
    setResultCopied(true)
    const textToCopy = resultTruncated ? `${formattedResult}\n…` : formattedResult
    copyToClipboard(textToCopy, () => setResultCopied(false))
  }

  return <div className="step tool-result">
    <ButtonBase className="tool-result-toggle" aria-expanded={resultExpanded} onClick={() => setResultExpanded(!resultExpanded)}>
      <span className={resultExpanded ? "chevron expanded" : "chevron"}><ChevronRight fontSize="inherit" /></span>
      <span>Ran {getToolNameFromPart(part)}</span>
    </ButtonBase>
    {resultExpanded && <div className="tool-result-block">
      <IconButton className="tool-result-copy" size="small" disabled={resultCopied} onClick={handleCopyResult}>
        {resultCopied ? <Check fontSize="inherit" /> : <FileCopy fontSize="inherit" />}
      </IconButton>
      <pre className="tool-result-content">
        {formattedResult}
        {resultTruncated && <span className="tool-result-truncated">{"\n…"}</span>}
      </pre>
    </div>}
  </div>
}

ResolvedToolLine.propTypes = {
  part: PropTypes.shape({
    type: PropTypes.string,
    toolName: PropTypes.string,
    state: PropTypes.string,
    errorText: PropTypes.string,
    output: PropTypes.shape({
      result: PropTypes.string,
      resultTruncated: PropTypes.bool,
    }),
  }).isRequired,
}

function LiveStep({ text }) {
  const revealedText = useSmoothRevealText(text, { isStreaming: true })
  return <div className="live-step">{revealedText}</div>
}

LiveStep.propTypes = {
  text: PropTypes.string.isRequired,
}

export function AiTeammateTurnSteps({ parts = [], liveText = "" }) {
  const timelineParts = parts
    .map((part, index) => ({ part, index }))
    .filter(({ part }) => part.type === "text" || isToolPart(part))
  if (timelineParts.length === 0 && !liveText) {
    return null
  }

  return <div className="turn-timeline">
    {timelineParts.map(({ part, index }) => {
      if (part.type === "text") {
        return <div className="step" key={`step-${index}`}>{part.text}</div>
      }
      return RUNNING_TOOL_STATES.has(part.state)
        ? <div className="running-tool" key={part.toolCallId}><span className="running-marker" />Running {getToolNameFromPart(part)}...</div>
        : <ResolvedToolLine part={part} key={part.toolCallId} />
    })}
    {liveText && <LiveStep text={liveText} />}
  </div>
}

AiTeammateTurnSteps.propTypes = {
  parts: PropTypes.arrayOf(TIMELINE_PART_SHAPE),
  liveText: PropTypes.string,
}

export function TrainOfThoughtStrip({ status, pulsing = false, className, children }) {
  const [expanded, setExpanded] = useState(false)

  return <div className={classNames("train-of-thought", className)}>
    <ButtonBase className="train-strip" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
      <span className={expanded ? "chevron expanded" : "chevron"}><ChevronRight fontSize="inherit" /></span>
      <span className={pulsing ? "strip-status pulsing" : "strip-status"}>{status}</span>
    </ButtonBase>
    <div className="train-of-thought-body" hidden={!expanded}>{children}</div>
  </div>
}

TrainOfThoughtStrip.propTypes = {
  status: PropTypes.node.isRequired,
  pulsing: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
}

export default function AiTeammateThinking({ parts = [], liveText = "" }) {
  const runningToolPart = parts.findLast((part) => isToolPart(part) && RUNNING_TOOL_STATES.has(part.state))
  const toolCallCount = countToolCalls(parts)
  const statusHead = runningToolPart ? `Running ${getToolNameFromPart(runningToolPart)}` : "Thinking"
  const statusText = toolCallCount > 0 ? `${statusHead} — ${formatToolCallCount(toolCallCount)}` : statusHead

  return <div className="ai-teammate-thinking">
    <TrainOfThoughtStrip status={statusText} pulsing={!!runningToolPart}>
      <AiTeammateTurnSteps parts={parts} liveText={liveText} />
    </TrainOfThoughtStrip>
    <div className="typing-row">
      <div className="thinking">
        <LoadingDots />
      </div>
    </div>
  </div>
}

AiTeammateThinking.propTypes = {
  parts: PropTypes.arrayOf(TIMELINE_PART_SHAPE),
  liveText: PropTypes.string,
}
